import { useCallback, useEffect, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { GlobalButton } from '../components/GlobalButton';
import { GlobalCheckbox } from '../components/GlobalCheckbox';
import { GlobalModal } from '../components/GlobalModal';
import GlobalSidebar from '../components/GlobalSidebar';
import { GlobalSwitch } from '../components/GlobalSwitch';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { RagIndexPanel } from '../components/RagIndexPanel';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { useProfilePhotoUpload } from '../hooks/useProfilePhotoUpload';
import { auth } from '../lib/firebase';
import {
  billingErrorMessage,
  createBillingPortalSession,
  createSubscriptionCheckout,
  type SubscriptionPlan,
} from '../lib/billing';
import {
  getFounderUpdateInbox,
  getFounderUpdateInboxErrorMessage,
} from '../lib/getFounderUpdateInbox';
import {
  requestAccountDeletion,
  requestAccountDeletionErrorMessage,
} from '../lib/requestAccountDeletion';
import { APP_VERSION } from '../version';
import authStyles from '../pages/auth/authPages.module.css';
import styles from './SettingsView.module.css';

async function clearCachesAndReload(): Promise<void> {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  }
  window.location.reload();
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M10 11v6M14 11v6M5 7l1 13a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1l1-13M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M22 2v6h-6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 8c-1.5-3-4.5-5-8-5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12.5l4 4 10-10"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CreditCardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M3 9.5h18" stroke="currentColor" strokeWidth="1.75" />
      <path d="M6.5 14.5h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

type SubscriptionPlanInfo = {
  id: SubscriptionPlan;
  title: string;
  price: string;
  period: string;
  caption: string;
  note: string;
  badge?: string;
};

const SUBSCRIPTION_PLANS: SubscriptionPlanInfo[] = [
  {
    id: 'monthly',
    title: 'Monthly',
    price: '€199',
    period: '/ month',
    caption: 'per month, billed monthly',
    note: 'Billed every month. Cancel anytime.',
  },
  {
    id: 'yearly',
    title: 'Yearly',
    price: '€1,990',
    period: '/ year',
    caption: '€165 / month, billed annually',
    note: 'Billed once a year — best value.',
    badge: '2 months free',
  },
];

const SUBSCRIPTION_FEATURES = [
  'Unlimited accounts, assets & holdings',
  'Daily net-worth snapshots & full history',
  'AI intelligence chat with cited answers',
  'Scenarios, projections & document analysis',
  'Automatic bank, founder-update & email syncing',
  'Personal & business domains · priority support',
];

function formatSubscriptionDate(ms?: number): string | null {
  if (!ms) return null;
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}

function subscriptionStatusLabel(status?: string): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'trialing':
      return 'Trial';
    case 'past_due':
      return 'Payment due';
    case 'canceled':
      return 'Cancelled';
    case 'unpaid':
      return 'Unpaid';
    default:
      return status ? status.replace(/_/g, ' ') : 'Active';
  }
}

export function SettingsView() {
  const { theme, toggleTheme } = useTheme();
  const {
    user,
    email,
    firstName,
    lastName,
    avatar,
    hasSubscription,
    subscriptionStatus,
    subscriptionPlan,
    subscriptionEnd,
    subscriptionCancelAtPeriodEnd,
    stripeCustomerId,
  } = useUser();
  const { uploading, error, upload } = useProfilePhotoUpload(user?.uid);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>('yearly');
  const [subscriptionSidebarOpen, setSubscriptionSidebarOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [checkoutNotice, setCheckoutNotice] = useState<'success' | 'cancelled' | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [inboxEmail, setInboxEmail] = useState<string | null>(null);
  const [inboxLoading, setInboxLoading] = useState(Boolean(user?.uid));
  const [inboxError, setInboxError] = useState<string | null>(null);
  const [inboxCopied, setInboxCopied] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const activePlan =
    SUBSCRIPTION_PLANS.find((plan) => plan.id === selectedPlan) ?? SUBSCRIPTION_PLANS[0];
  const displayName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const photoSrc = (avatar?.trim() || user?.photoURL || '').trim();
  const isDarkMode = theme === 'dark';
  const uploadIcon = (
    <img
      src={isDarkMode ? '/assets/icons/icloud.and.arrow.up.svg' : '/assets/icons/icloud.and.arrow.up.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );
  const copyIcon = (
    <img
      src={
        isDarkMode
          ? '/assets/icons/document.on.document.svg'
          : '/assets/icons/document.on.document.black.svg'
      }
      alt=""
      width={16}
      height={16}
    />
  );

  const loadFounderUpdateInbox = useCallback(async () => {
    if (!user?.uid) {
      setInboxEmail(null);
      setInboxLoading(false);
      setInboxError(null);
      return;
    }

    setInboxLoading(true);
    setInboxError(null);

    try {
      const result = await getFounderUpdateInbox();
      setInboxEmail(result.emailAddress);
    } catch (err) {
      setInboxEmail(null);
      setInboxError(getFounderUpdateInboxErrorMessage(err));
    } finally {
      setInboxLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    void loadFounderUpdateInbox();
  }, [loadFounderUpdateInbox]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success' || checkout === 'cancelled') {
      setCheckoutNotice(checkout);
      params.delete('checkout');
      const query = params.toString();
      const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleSubscribe = async () => {
    if (checkoutLoading) return;
    setBillingError(null);
    setCheckoutLoading(true);
    try {
      const checkoutUrl = await createSubscriptionCheckout(selectedPlan);
      window.location.href = checkoutUrl;
    } catch (err) {
      setBillingError(billingErrorMessage(err));
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (portalLoading) return;
    setBillingError(null);
    setPortalLoading(true);
    try {
      const url = await createBillingPortalSession();
      window.location.href = url;
    } catch (err) {
      setBillingError(billingErrorMessage(err));
      setPortalLoading(false);
    }
  };

  const handleCopyInboxEmail = async () => {
    if (!inboxEmail) return;

    try {
      await navigator.clipboard.writeText(inboxEmail);
      setInboxCopied(true);
      window.setTimeout(() => setInboxCopied(false), 2000);
    } catch {
      setInboxError('Could not copy to clipboard. Select the address and copy manually.');
    }
  };

  const handleRefreshApp = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await clearCachesAndReload();
  };

  const openDeleteModal = () => {
    setDeleteAcknowledged(false);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModalOpen(false);
  };

  const handleDeleteAccount = async () => {
    if (deleting || !deleteAcknowledged) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await requestAccountDeletion();
      await signOut(auth);
    } catch (err) {
      setDeleteError(requestAccountDeletionErrorMessage(err));
      setDeleting(false);
    }
  };

  return (
    <ViewPage title="Settings">
      <div className={styles.root}>
      <ViewSectionHeader
        eyebrow="Preferences"
        title="Settings"
        subtitle="Manage your account and app preferences."
      />

      <section className="panel settings-panel">
        <div className="panel-head">
          <h2>Account</h2>
        </div>
        <div className="settings-row">
          <div>
            <p className="settings-row-label">{displayName || '—'}</p>
            <p className="muted settings-row-hint">{email || '—'}</p>
            {error ? <p className="settings-avatar-error">{error}</p> : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="settings-avatar-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
              e.target.value = '';
            }}
          />
          <GlobalButton
            variant="secondary"
            autoWidth
            icon={uploadIcon}
            isLoading={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {photoSrc ? 'Change photo' : 'Upload photo'}
          </GlobalButton>
        </div>
      </section>

      <section className="panel settings-panel">
        <div className="panel-head">
          <h2>Appearance</h2>
        </div>
        <div className="settings-row">
          <div>
            <p className="settings-row-label">Dark mode</p>
            <p className="muted settings-row-hint">Use a dark color scheme across the app.</p>
          </div>
          <GlobalSwitch checked={theme === 'dark'} onChange={toggleTheme} />
        </div>
      </section>

      <section className="panel settings-panel">
        <div className="panel-head">
          <h2>Language</h2>
        </div>
        <div className="settings-row">
          <div>
            <p className="settings-row-label">Display language</p>
            <p className="muted settings-row-hint">Choose the language used in the interface.</p>
          </div>
          <LanguageSwitch />
        </div>
      </section>

      <section className="panel settings-panel" aria-labelledby="app-updates-heading">
        <div className="panel-head">
          <h2 id="app-updates-heading">App updates</h2>
        </div>
        <div className="settings-row">
          <div>
            <p className="settings-row-label">Check for updates</p>
            <p className="muted settings-row-hint">
              Current version: v{APP_VERSION}. If the app added to your home screen is out of date,
              tap Refresh to clear the cache and load the latest version.
            </p>
          </div>
          <GlobalButton
            type="button"
            variant="secondary"
            autoWidth
            icon={<RefreshIcon />}
            isLoading={refreshing}
            disabled={refreshing}
            onClick={handleRefreshApp}
          >
            Refresh app
          </GlobalButton>
        </div>
      </section>

      <section className="panel settings-panel" aria-labelledby="subscription-heading">
        <div className="panel-head">
          <h2 id="subscription-heading">Subscription</h2>
        </div>

        {checkoutNotice === 'success' ? (
          <div className={`${authStyles.banner} ${authStyles.bannerSuccess}`} role="status">
            <p style={{ margin: 0 }}>
              Thanks for subscribing! Your plan will appear here within a few moments.
            </p>
          </div>
        ) : null}
        {checkoutNotice === 'cancelled' ? (
          <div className={`${authStyles.banner}`} role="status">
            <p style={{ margin: 0 }}>Checkout was cancelled. You can subscribe whenever you're ready.</p>
          </div>
        ) : null}

        <div className="settings-row">
          <div>
            <p className="settings-row-label">Upgrade to Perfina Pro</p>
            {hasSubscription ? (
              <p className="settings-row-hint" style={{ color: 'var(--shamrock-green)', fontWeight: 650 }}>
                {subscriptionStatusLabel(subscriptionStatus)}
                {subscriptionPlan ? ` · ${subscriptionPlan === 'yearly' ? 'Yearly' : 'Monthly'} plan` : ''}
                {subscriptionCancelAtPeriodEnd
                  ? ` — ends ${formatSubscriptionDate(subscriptionEnd) ?? 'at the end of the period'}`
                  : formatSubscriptionDate(subscriptionEnd)
                    ? ` — renews ${formatSubscriptionDate(subscriptionEnd)}`
                    : ''}
              </p>
            ) : (
              <p className="muted settings-row-hint">Unlock the full wealth-management toolkit.</p>
            )}
          </div>
          {hasSubscription ? (
            <GlobalButton
              type="button"
              variant="secondary"
              autoWidth
              icon={<CreditCardIcon />}
              isLoading={portalLoading}
              disabled={portalLoading || !stripeCustomerId}
              onClick={handleManageSubscription}
            >
              Manage subscription
            </GlobalButton>
          ) : (
            <GlobalButton
              type="button"
              autoWidth
              icon={<CreditCardIcon />}
              onClick={() => setSubscriptionSidebarOpen(true)}
            >
              Purchase
            </GlobalButton>
          )}
        </div>

        {billingError ? (
          <div className={`${authStyles.banner} ${authStyles.bannerError}`} role="alert" style={{ marginTop: 16 }}>
            <p style={{ margin: 0 }}>{billingError}</p>
          </div>
        ) : null}
      </section>

      <GlobalSidebar
        isOpen={subscriptionSidebarOpen}
        onClose={() => {
          if (!checkoutLoading) setSubscriptionSidebarOpen(false);
        }}
      >
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Upgrade to Perfina Pro</h2>

          <div
            className="subscriptionToggle"
            role="radiogroup"
            aria-label="Billing period"
          >
            {SUBSCRIPTION_PLANS.map((plan) => {
              const isSelected = selectedPlan === plan.id;
              return (
                <button
                  key={plan.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  className={`subscriptionToggle__option${isSelected ? ' is-active' : ''}`}
                  disabled={checkoutLoading}
                  onClick={() => setSelectedPlan(plan.id)}
                >
                  <span>{plan.title}</span>
                  {plan.badge ? (
                    <span className="subscriptionToggle__badge">{plan.badge}</span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="subscriptionCard">
            <span className="subscriptionCard__badge">Perfina Pro</span>
            <p className="subscriptionCard__plan">{activePlan.title} plan</p>
            <div className="subscriptionCard__amount">
              <span className="subscriptionCard__price">{activePlan.price}</span>
              <span className="subscriptionCard__period">{activePlan.period}</span>
            </div>
            <p className="subscriptionCard__caption">{activePlan.caption}</p>
            <p className="subscriptionCard__note">{activePlan.note}</p>

            <div className="subscriptionCard__divider" />

            <p className="subscriptionCard__listHead">Everything included</p>
            <ul className="subscriptionCard__features">
              {SUBSCRIPTION_FEATURES.map((feature) => (
                <li key={feature}>
                  <span className="subscriptionCard__check" aria-hidden="true">
                    <CheckIcon />
                  </span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="subscriptionCard__cta">
              <GlobalButton
                type="button"
                icon={<CreditCardIcon />}
                isLoading={checkoutLoading}
                disabled={checkoutLoading}
                onClick={handleSubscribe}
              >
                {`Subscribe ${selectedPlan === 'yearly' ? 'yearly' : 'monthly'}`}
              </GlobalButton>
            </div>

            <p className="subscriptionCard__finePrint">
              Secure checkout via Stripe. Cancel anytime from your account.
            </p>

            {billingError ? (
              <div
                className={`${authStyles.banner} ${authStyles.bannerError}`}
                role="alert"
                style={{ marginTop: 16 }}
              >
                <p style={{ margin: 0 }}>{billingError}</p>
              </div>
            ) : null}
          </div>
        </div>
      </GlobalSidebar>

      <RagIndexPanel />

      <section className="panel settings-panel" aria-labelledby="founder-update-inbox-heading">
        <div className="panel-head">
          <h2 id="founder-update-inbox-heading">Founder update inbox</h2>
        </div>

        <div className={styles.founderInboxRow}>
          <div className={styles.founderInboxBody}>
            <p className="settings-row-label">Inbox address</p>
            <p className="muted settings-row-hint">
              Forward founder updates here; Perfina will match them to companies and extract KPIs.
            </p>
            {inboxLoading ? (
              <p className="muted settings-row-hint" aria-live="polite">
                Loading inbox address…
              </p>
            ) : inboxEmail ? (
              <code className={styles.founderInboxCode}>{inboxEmail}</code>
            ) : null}
          </div>
          <GlobalButton
            type="button"
            variant="secondary"
            autoWidth
            className={styles.copyButton}
            icon={copyIcon}
            disabled={inboxLoading || !inboxEmail}
            onClick={handleCopyInboxEmail}
          >
            {inboxCopied ? 'Copied' : 'Copy address'}
          </GlobalButton>
        </div>

        {inboxError ? (
          <div className={`${authStyles.banner} ${authStyles.bannerError}`} role="alert">
            <p style={{ margin: 0 }}>{inboxError}</p>
            <div className={styles.inboxErrorActions}>
              <GlobalButton
                type="button"
                variant="secondary"
                autoWidth
                disabled={inboxLoading}
                isLoading={inboxLoading}
                onClick={loadFounderUpdateInbox}
              >
                Try again
              </GlobalButton>
            </div>
          </div>
        ) : null}
      </section>

      <section className="panel settings-panel" aria-labelledby="danger-zone-heading">
        <div className="panel-head">
          <h2 id="danger-zone-heading">Danger zone</h2>
        </div>

        <div className="settings-row">
          <div>
            <p className="settings-row-label">Delete account</p>
            <p className="muted settings-row-hint">
              Deactivates your account and schedules it for permanent deletion.
            </p>
          </div>
          <GlobalButton
            type="button"
            variant="secondary"
            destructive
            autoWidth
            icon={<TrashIcon />}
            onClick={openDeleteModal}
          >
            Delete account
          </GlobalButton>
        </div>
      </section>

      <GlobalModal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        title="Delete account"
        dismissible={!deleting}
        footer={
          <>
            <GlobalButton
              type="button"
              variant="secondary"
              autoWidth
              disabled={deleting}
              onClick={closeDeleteModal}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="secondary"
              destructive
              autoWidth
              icon={<TrashIcon />}
              isLoading={deleting}
              disabled={deleting || !deleteAcknowledged}
              onClick={handleDeleteAccount}
            >
              Permanently delete account
            </GlobalButton>
          </>
        }
      >
        <p className="muted" style={{ margin: 0 }}>
          You'll be signed out immediately and won't be able to sign back in. This cannot be undone —
          contact support if you change your mind.
        </p>

        {deleteError ? (
          <div
            className={`${authStyles.banner} ${authStyles.bannerError}`}
            role="alert"
            style={{ marginTop: 16 }}
          >
            {deleteError}
          </div>
        ) : null}

        <div style={{ marginTop: 16 }}>
          <GlobalCheckbox
            checked={deleteAcknowledged}
            onChange={(e) => setDeleteAcknowledged(e.target.checked)}
            disabled={deleting}
            label="I understand my account will be deactivated and permanently deleted."
          />
        </div>
      </GlobalModal>
      </div>
    </ViewPage>
  );
}
